import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Amplify from 'aws-amplify';
import awsconfig from '../../aws-config';
import { Breadcumb, VisaoGeralUchasEbas } from '../../components';
import { AplicacoesCliente } from './content';
import { ZonasClientesMapa } from '../dashboard/content';
import * as s from './styled-customer';
import { getStatus, notifyError, GetColor } from '../../utils';
import { getDataDashboard } from '../../services';

Amplify.configure(awsconfig);

// Agrega contadores de status (W/E/O/OFF) por chave somando todos os
// appliances de todos os parques. Usado pra derivar status global do
// cliente no header (rodando se >0 em O, alarme se >0 em E, etc).
const sumObjectsByKey = (objsArr) => {
  const newObj = { W: 0, E: 0, O: 0, OFF: 0 };
  objsArr.forEach((obj) => {
    Object.keys(newObj).forEach((key) => {
      newObj[key] += obj[key] || 0;
    });
  });
  return newObj;
};

const Parque = () => {
  const { user } = useSelector((state) => state.usuario);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const parques = useSelector((state) => state.segundoNivel.mapa.parques);
  const parqueSelecionado = useSelector(
    (state) => state.segundoNivel.parqueSelecionado
  );
  const clienteSelecionado = useSelector(
    (state) => state.segundoNivel.clienteSelecionado
  );
  const uchas = useSelector((state) => state.segundoNivel.uchas);
  const ebas = useSelector((state) => state.segundoNivel.ebas);
  // Lido so pra repassar no dispatch de deselecao (o reducer
  // setSelecionados sobrescreve filterStatus sem fallback).
  const filterStatus = useSelector((state) => state.segundoNivel.filterStatus);
  const history = useHistory();
  const historyParque = history.location.state;
  // Ref da coluna de aplicacoes (cards). Clicar FORA dela desseleciona
  // (volta os numeros do AGORA pro cliente inteiro). Cliques em cards
  // selecionam; cliques em markers do mapa re-selecionam no proprio
  // handler (apos o mousedown limpar) — entao markers continuam
  // funcionando, e clicar no mapa vazio / donut / pagina desseleciona.
  const cardsRef = useRef(null);

  // Conjunto COMPLETO de parques do cliente — alimenta mapa, lista,
  // status e o donut. NAO colapsa pro parque selecionado/navegado: a
  // selecao afeta so o AGORA (agoraParques) e o destaque. Antes, entrar
  // via um parque (historyParque.appliance) prendia tudo nesse 1 parque,
  // sumindo o resto do mapa/lista. Fallback pro contexto de navegacao so
  // enquanto mapa.parques (via BUSCA_ZONAS_CUSTOMER) ainda nao carregou.
  const parquesSelecionadoReduce = useMemo(() => {
    if (Array.isArray(parques) && parques.length) return parques;
    if (historyParque?.appliance) return [historyParque];
    if (parqueSelecionado && parqueSelecionado.id) return [parqueSelecionado];
    return [];
  }, [parques, historyParque, parqueSelecionado]);

  // Chave estavel do CONJUNTO de parques (ids ordenados). A referencia
  // da array oscila a cada poll/render do saga mesmo sem o conteudo
  // mudar — usar a array como dep do effect de subscription criaria loop.
  // A length sozinha (dep anterior) nao re-disparava ao trocar de cliente
  // com o MESMO numero de parques, deixando AGORA (onTime/vazao) congelado.
  // Os ids cobrem ambos: estavel quando o conjunto e o mesmo, muda quando
  // o cliente/parque selecionado muda.
  const parquesKey = useMemo(
    () =>
      (Array.isArray(parquesSelecionadoReduce) ? parquesSelecionadoReduce : [])
        .map((p) => p && p.id)
        .sort()
        .join(','),
    [parquesSelecionadoReduce]
  );

  // Escopo do AGORA (onTime/vazao). O donut/summary (getDataDashboard)
  // escopa por parque selecionado, mas o AGORA somava SEMPRE todos os
  // parques do cliente (parques || parqueSelecionado caia em parques) —
  // entao ao selecionar uma aplicacao/parque o donut mudava e o AGORA
  // nao. Aqui escopamos o AGORA ao parque selecionado quando ha um,
  // mantendo o cliente inteiro quando nao ha. (O mapa continua usando
  // parquesSelecionadoReduce — todos os parques.)
  const agoraParques = useMemo(() => {
    // Escopo do AGORA dirigido SO pela selecao explicita (parqueSelecionado).
    // Sem selecao (ou apos deselecionar clicando fora) volta ao cliente
    // inteiro = numeros gerais. Nao usa historyParque aqui: a entrada via
    // um parque ja seta parqueSelecionado, e desselecionar deve voltar ao
    // geral mesmo tendo entrado por um parque.
    if (
      parqueSelecionado &&
      parqueSelecionado.id &&
      Array.isArray(parqueSelecionado.appliance)
    ) {
      return [parqueSelecionado];
    }
    return Array.isArray(parquesSelecionadoReduce)
      ? parquesSelecionadoReduce
      : [];
  }, [parqueSelecionado, parquesSelecionadoReduce]);

  const agoraKey = useMemo(
    () => agoraParques.map((p) => p && p.id).sort().join(','),
    [agoraParques]
  );

  useEffect(() => {
    const customerId = user?.customer?.id || clienteSelecionado?.id;
    if (customerId) {
      dispatch({
        type: 'SEGUNDO/BUSCA_ZONAS_CUSTOMER',
        customerId: String(customerId),
      });
    }
  }, [user, clienteSelecionado, dispatch]);

  useEffect(() => {
    if (parquesSelecionadoReduce && parquesSelecionadoReduce.length) {
      // SEGUNDO alimenta o AGORA (onTime/vazao) — escopado ao parque
      // selecionado (agoraParques) pra ficar consistente com o donut.
      dispatch({
        type: 'SEGUNDO/ABRE_CONEXAO_THINGS',
        parques: agoraParques,
      });
      // PRIMEIRO alimenta state.primeiroNivel.parqueRealtime que o
      // PracasMapa (embedded) le pra montar os badges de pre-info
      // acima dos markers (potencia UCHA / vazao EBA). Usa TODOS os
      // parques (parquesSelecionadoReduce) pra nao sumir badge no mapa.
      dispatch({
        type: 'PRIMEIRO/ABRE_CONEXAO_THINGS',
        parques: parquesSelecionadoReduce,
      });
      setIsLoading(false);
    }
    // Cleanup: fecha as conexoes do PRIMEIRO nivel quando os parques
    // mudam ou ao desmontar — sem isso, mudar de cliente ou voltar
    // pro /dashboard deixava WebSockets vivos.
    // Nota: SEGUNDO/FECHA_CONEXOES nao tem handler hoje (saga so faz
    // getIOT one-shot, nao mantem subscriptions), entao nao
    // dispatcha. Quando o SEGUNDO ganhar subscriptions vivas, adicionar
    // o dispatch correspondente aqui.
    return () => {
      dispatch({ type: 'PRIMEIRO/FECHA_CONEXOES' });
    };
    // Re-dispara quando o CONJUNTO de parques muda (parquesKey = ids
    // ordenados) — re-escopa a subscription/soma ao trocar de cliente,
    // inclusive quando o novo cliente tem o mesmo numero de parques.
    // agoraKey: re-dispara o SEGUNDO (AGORA) ao mudar o parque
    // selecionado. parquesKey: re-dispara ao mudar o conjunto (troca de
    // cliente). parquesKey e estavel quando o conteudo nao muda (evita o loop da
    // array-ref que oscila a cada poll do saga).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parquesKey, agoraKey, dispatch]);

  // Donut/totais (VisaoGeralUchasEbas: contagem de UCHAs/EBAs, energia).
  // /dashboard/customer e a visao do CLIENTE — entao escopamos SEMPRE no
  // cliente, mesmo com um parque selecionado. Antes priorizava
  // parqueSelecionado, colapsando o donut ("TOTAL DE 1 UCHAS") e a lista
  // ao selecionar uma aplicacao. A selecao agora afeta so o AGORA.
  useEffect(() => {
    let mounted = true;
    const customerId = user?.customer?.id || clienteSelecionado?.id;
    let id;
    let type;
    if (customerId) {
      id = customerId;
      type = 'customer';
    } else if (parqueSelecionado && parqueSelecionado.id) {
      // Fallback: sem contexto de cliente, usa o parque (ex.: navegou
      // direto via um parque sem customer associado).
      id = parqueSelecionado.id;
      type = 'park';
    }
    if (!id || !type) return undefined;
    getDataDashboard(id, type)
      .then(({ data }) => {
        if (!mounted) return;
        dispatch({
          type: 'SEGUNDO/SET_VALORES_UCHAS_EBAS',
          uchas: data.ucha,
          ebas: data.eba,
        });
      })
      .catch(() => {
        if (mounted) notifyError('Erro ao buscar os valores');
      });
    return () => {
      mounted = false;
    };
  }, [parqueSelecionado, clienteSelecionado, user, dispatch]);

  // Clicar FORA dos cards desseleciona (AGORA volta ao cliente inteiro).
  // Usa mousedown+mouseup e so desseleciona num CLIQUE LIMPO (sem arrasto)
  // — assim arrastar/pan o mapa NAO desseleciona. Markers do mapa
  // re-selecionam no proprio click (dispara apos o mouseup), entao clicar
  // num marker ainda seleciona; clicar no mapa vazio / donut / pagina
  // (sem arrastar) desseleciona.
  useEffect(() => {
    if (!parqueSelecionado || !parqueSelecionado.id) return undefined;
    const insideCards = (el) =>
      cardsRef.current && cardsRef.current.contains(el);
    let down = null; // { x, y, outside }
    const onDown = (e) => {
      down = { x: e.clientX, y: e.clientY, outside: !insideCards(e.target) };
    };
    const onUp = (e) => {
      if (!down || !down.outside) {
        down = null;
        return;
      }
      // Ignora arrasto (pan do mapa, selecao de texto): so clique limpo.
      const moved =
        Math.abs(e.clientX - down.x) > 5 || Math.abs(e.clientY - down.y) > 5;
      down = null;
      if (moved || insideCards(e.target)) return;
      dispatch({
        type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
        parqueSelecionado: {},
        // preserva o resto (o reducer sobrescreve filterStatus sem fallback)
        filterStatus,
      });
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('mouseup', onUp);
    };
  }, [parqueSelecionado, filterStatus, dispatch]);

  // Status global do cliente — se parqueSelecionado tem status proprio
  // (objeto contagem W/E/O/OFF), usa direto; senao agrega todos os
  // appliances de todos os parques visiveis. Memoizado pra evitar
  // refazer .map().flat().map() a cada render (subscriptions IoT
  // disparam render em alta frequencia em modo CCO/TV).
  // IMPORTANTE: precisa ficar ANTES do early return do skeleton —
  // hooks nao podem ser chamados condicionalmente.
  const aggregatedStatus = useMemo(
    () =>
      parquesSelecionadoReduce?.status ||
      sumObjectsByKey(
        (Array.isArray(parquesSelecionadoReduce)
          ? parquesSelecionadoReduce
          : []
        )
          .map(({ appliance }) => appliance || [])
          .flat(1)
          .map(({ status }) => status || {})
      ),
    [parquesSelecionadoReduce]
  );

  // Flatten todos os appliances dos parques visiveis pra contagem do
  // footer do sidebar (espelha a logica do AplicacoesCliente interno).
  // Memoizado — mesma razao do aggregatedStatus acima. Tambem precisa
  // ficar antes do early return.
  const allAppliances = useMemo(
    () =>
      (Array.isArray(parquesSelecionadoReduce)
        ? parquesSelecionadoReduce
        : []
      ).flatMap(({ appliance }) => appliance || []),
    [parquesSelecionadoReduce]
  );

  // Skeleton enquanto a primeira fetch nao volta. Layout ja na nova
  // shape (header bar + grid 1fr|360) com blocos shimmer.
  if (!parquesSelecionadoReduce.length || isLoading) {
    return (
      <s.Container>
        <s.HeaderBar>
          <s.HeaderBreadcrumbRow>
            <s.SkeletonBlock style={{ width: 140, height: 16 }} />
          </s.HeaderBreadcrumbRow>
          <s.HeaderInfoRow>
            <s.SkeletonBlock style={{ width: 180, height: 22 }} />
            <s.SkeletonBlock style={{ width: 130, height: 26 }} />
          </s.HeaderInfoRow>
        </s.HeaderBar>
        <s.MainGrid>
          <s.FormColumn>
            <s.MapSection>
              <s.SkeletonBlock style={{ flex: 1, minHeight: 360 }} />
            </s.MapSection>
          </s.FormColumn>
          <s.SidebarColumn>
            <s.SkeletonBlock style={{ flex: 1, minHeight: 200, margin: 16 }} />
          </s.SidebarColumn>
        </s.MainGrid>
      </s.Container>
    );
  }

  const statusLabel = getStatus(aggregatedStatus);
  const statusColor = GetColor(statusLabel);

  // Nome do cliente — mesma cadeia de fallback do Overview legado:
  //   1) usuario logado e CUSTOMER (user.customer.name)
  //   2) admin/manager selecionou um cliente (clienteSelecionado.name)
  //   3) parqueSelecionado tem o nome do parque (caso clicaram num marker)
  //   4) primeiro parque da lista (parquesSelecionadoReduce[0].name)
  //   5) fallback generico
  const customerName =
    (user && user.customer && user.customer.name) ||
    (clienteSelecionado && clienteSelecionado.name) ||
    (parqueSelecionado && parqueSelecionado.name) ||
    (!Array.isArray(parquesSelecionadoReduce) &&
      parquesSelecionadoReduce &&
      parquesSelecionadoReduce.name) ||
    (Array.isArray(parquesSelecionadoReduce) &&
      parquesSelecionadoReduce[0] &&
      parquesSelecionadoReduce[0].customer &&
      parquesSelecionadoReduce[0].customer.name) ||
    'Cliente';

  const uchasEmpty = !!uchas && 'total' in uchas && uchas.total === 0;
  const ebasEmpty = !!ebas && 'total' in ebas && ebas.total === 0;

  const hasUchas = !uchasEmpty;
  const hasEbas = !ebasEmpty;
  const showPair = hasUchas && hasEbas;

  // Rotulo de escopo do AGORA: quando um parque esta selecionado, mostra
  // qual e (pra deixar claro que os numeros sao dele, nao do cliente).
  const selectedScopeLabel =
    parqueSelecionado && parqueSelecionado.id
      ? parqueSelecionado.name ||
        (parqueSelecionado.appliance &&
          parqueSelecionado.appliance[0] &&
          parqueSelecionado.appliance[0].description) ||
        'Parque selecionado'
      : null;
  const clearSelecao = () =>
    dispatch({
      type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
      parqueSelecionado: {},
      filterStatus,
    });

  const totalAplicacoes = allAppliances.length;
  const aplicacoesLabel =
    totalAplicacoes === 1 ? 'aplicação cadastrada' : 'aplicações cadastradas';

  return (
    <s.Container>
      <s.HeaderBar>
        {/* Row 1: breadcrumb solo, separado da info do cliente por
            hairline horizontal. */}
        <s.HeaderBreadcrumbRow>
          <s.HeaderBreadcrumb>
            <Breadcumb
              paths={
                user.customer
                  ? [{ pathName: 'Cliente', to: '/customer' }]
                  : [
                      {
                        pathName: 'Dashboard',
                        to: '/dashboard',
                        action: () => dispatch({ type: 'SEGUNDO/RESET' }),
                      },
                      { pathName: 'Cliente', to: '/dashboard/customer' },
                    ]
              }
            />
          </s.HeaderBreadcrumb>
        </s.HeaderBreadcrumbRow>

        {/* Row 2: nome do cliente (pin + texto) + status chip lado a
            lado, gap 20px. Ambos se referem ao MESMO cliente — agrupar
            esclarece o contexto (antes status ficava na direita
            distante do nome). */}
        <s.HeaderInfoRow>
          <s.HeaderName>
            <s.HeaderPin>
              {/* lucide:map-pin */}
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </s.HeaderPin>
            <span>{customerName}</span>
          </s.HeaderName>
          {statusLabel && (
            <s.StatusChip
              $color={statusColor}
              title={`Status: ${statusLabel}`}
            >
              <s.StatusDot $color={statusColor} aria-hidden="true" />
              {statusLabel}
            </s.StatusChip>
          )}
        </s.HeaderInfoRow>
      </s.HeaderBar>

      <s.MainGrid>
        <s.FormColumn>
          {/* Mapa — fica intacto no comportamento (props identicas).
              Apenas o card wrapper antigo foi removido pra deixar
              o canvas flush com as bordas da MapSection. */}
          <s.MapSection id="client-map-wrapper">
            <ZonasClientesMapa
              embedded
              markers={parquesSelecionadoReduce}
              setMarkers={() => {}}
              selectedClient={clienteSelecionado || {}}
              setSelectedClient={() => {}}
            />
          </s.MapSection>

          {/* Barra de escopo — so quando um parque esta selecionado.
              Deixa claro que o AGORA reflete aquele parque + botao
              "Ver todos" pra voltar aos numeros do cliente inteiro. */}
          {selectedScopeLabel && (
            <s.ScopeBar>
              <span className="lbl">
                Exibindo agora: <strong>{selectedScopeLabel}</strong>
              </span>
              <button type="button" onClick={clearSelecao}>
                {/* lucide:x */}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
                Ver todos
              </button>
            </s.ScopeBar>
          )}

          {/* MetricsSection — donut + AGORA + ENERGIA via VisaoGeralUchasEbas.
              Componente ja renderiza TagTitle ("TOTAL DE N {tipo} · Geração/
              Bombeamento · atualização contínua") por dentro. Quando UCHAs
              e EBAs ambos presentes, ficam lado a lado (data-pair) com
              hairline vertical entre eles. */}
          <s.MetricsSection data-pair={showPair ? 'true' : 'false'}>
            {hasUchas && (
              <VisaoGeralUchasEbas
                tipo="uchas"
                data={uchas}
                color="orange"
                compact={showPair}
              />
            )}
            {hasEbas && (
              <VisaoGeralUchasEbas
                tipo="ebas"
                data={ebas}
                color="blue"
                compact={showPair}
              />
            )}
          </s.MetricsSection>
        </s.FormColumn>

        <s.SidebarColumn ref={cardsRef}>
          <s.SidebarHeader>
            <s.SidebarTitle>Aplicações</s.SidebarTitle>
          </s.SidebarHeader>

          <s.SidebarBody>
            {/* hideTotal — o contador "N aplicações cadastradas" e
                renderizado externamente no SidebarFooter pra ficar fixo
                no rodape do sidebar (alinhado com a barra de status). */}
            <AplicacoesCliente hideTotal />
          </s.SidebarBody>

          {totalAplicacoes > 0 && (
            <s.SidebarFooter>
              {totalAplicacoes} {aplicacoesLabel}
            </s.SidebarFooter>
          )}
        </s.SidebarColumn>
      </s.MainGrid>
    </s.Container>
  );
};

export default Parque;
